// =============================================================================
// OAUTH SERVICE
// =============================================================================
// Ubicación: src/services/OAuthService.ts
// =============================================================================

import { HTTP_METHODS } from '../constants/httpMethods';
import { apiService } from './ApiService';
import { ApiResponse } from '../interfaces/responses/ApiResponse';
import { API_ENDPOINTS } from '../constants/apiEndpoints';

// OAuth login now lives on HeavyDutyMachineryApi's own AuthController.
const baseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

// =============================================================================
// INTERFACES
// =============================================================================

export interface OAuthLoginResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    profilePicture?: string;
  };
  isNewUser: boolean;
}

export interface LinkedProvider {
  provider: string;
  providerKey: string;
  linkedAt: string;
}

// =============================================================================
// GOOGLE OAUTH
// =============================================================================

export const loginWithGoogle = async (idToken: string): Promise<OAuthLoginResponse> => {
  const response = await apiService<ApiResponse<OAuthLoginResponse>>({
    url: `${API_ENDPOINTS.OAUTH_GOOGLE}`,
    method: HTTP_METHODS.POST,
    body: { idToken, applicationId: 0 },
    baseUrl: baseUrl
  });

  if (response.success) {
    return response.data.data;
  } else {
    throw new Error(response.error || 'Google authentication failed');
  }
};

// =============================================================================
// MICROSOFT OAUTH
// =============================================================================

export const loginWithMicrosoft = async (accessToken: string): Promise<OAuthLoginResponse> => {
  const response = await apiService<ApiResponse<OAuthLoginResponse>>({
    url: `${API_ENDPOINTS.AUTH}/oauth/microsoft`,
    method: HTTP_METHODS.POST,
    body: { accessToken, applicationId: 0 },
    baseUrl: baseUrl
  });

  if (response.success) {
    return response.data.data;
  } else {
    throw new Error(response.error || 'Microsoft authentication failed');
  }
};

// =============================================================================
// GITHUB OAUTH
// =============================================================================

export const loginWithGitHub = async (code: string): Promise<OAuthLoginResponse> => {
  const response = await apiService<ApiResponse<OAuthLoginResponse>>({
    url: `${API_ENDPOINTS.AUTH}/oauth/github`,
    method: HTTP_METHODS.POST,
    body: { code, applicationId: 0 },
    baseUrl: baseUrl
  });

  if (response.success) {
    return response.data.data;
  } else {
    throw new Error(response.error || 'GitHub authentication failed');
  }
};

// =============================================================================
// FACEBOOK OAUTH
// =============================================================================

export const loginWithFacebook = async (accessToken: string): Promise<OAuthLoginResponse> => {
  const response = await apiService<ApiResponse<OAuthLoginResponse>>({
    url: `${API_ENDPOINTS.AUTH}/oauth/facebook`,
    method: HTTP_METHODS.POST,
    body: { accessToken, applicationId: 0 },
    baseUrl: baseUrl
  });

  if (response.success) {
    return response.data.data;
  } else {
    throw new Error(response.error || 'Facebook authentication failed');
  }
};



// =============================================================================
// GET LINKED PROVIDERS
// =============================================================================

export const getLinkedProviders = async (): Promise<LinkedProvider[]> => {
  const token = localStorage.getItem('accessToken');
  
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await apiService<ApiResponse<LinkedProvider[]>>({
    url: `${API_ENDPOINTS.AUTH}/oauth/providers`,
    method: HTTP_METHODS.GET,
    baseUrl: baseUrl
  });

  if (response.success) {
    return response.data.data;
  } else {
    throw new Error(response.error || 'Failed to get linked providers');
  }
};

// =============================================================================
// UNLINK PROVIDER
// =============================================================================

export const unlinkProvider = async (provider: string): Promise<void> => {
  const token = localStorage.getItem('accessToken');
  
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await apiService<ApiResponse<void>>({
    url: `${API_ENDPOINTS.AUTH}/oauth/${provider}`,
    method: HTTP_METHODS.DELETE,
    baseUrl: baseUrl
  });

  if (!response.success) {
    throw new Error(response.error || 'Failed to unlink provider');
  }
};

// =============================================================================
// HELPER - GET GITHUB OAUTH AUTHORIZATION URL
// =============================================================================

export const getGitHubAuthUrl = (): string => {
  const clientId = process.env.REACT_APP_GITHUB_CLIENT_ID;
  const redirectUri = `${window.location.origin}/oauth/callback`;
  const state = Math.random().toString(36).substring(7);
  
  localStorage.setItem('github_oauth_state', state);

  const params = new URLSearchParams({
    client_id: clientId || '',
    redirect_uri: redirectUri,
    scope: 'user:email',
    state,
  });

  return `https://github.com/login/oauth/authorize?${params.toString()}`;
};

// =============================================================================
// VERIFY STATE (CSRF Protection)
// =============================================================================

export const verifyOAuthState = (provider: string, state: string): boolean => {
  const savedState = localStorage.getItem(`${provider.toLowerCase()}_oauth_state`);
  localStorage.removeItem(`${provider.toLowerCase()}_oauth_state`);
  return state === savedState;
};